import { useEffect, useRef, useState } from 'react';
import type { Province } from '../../types/province';
import type { StatusBannerTone } from '../shell/StatusBanner';
import { useDragonStatus } from '../../hooks/useDragonStatus';
import { useKingdomRelationships } from '../../hooks/useKingdomRelationships';
import { isProtected } from '../../utils/protection';

/**
 * SF-122 Component 3: `<StatusBanner>` stack resolution logic, shared by `MobileThroneShell` and
 * `DesktopThroneShell` (both render the identical resolved set — only the surrounding chrome
 * differs, per Key Implementation Note 1's "divergent chrome" principle, which is about DOM
 * structure, not about re-deriving the same priority/viewport-budget/live-announcement logic
 * twice).
 *
 * Resolves zero or more banners, each independently evaluated, in priority order
 * dragon → war/ceasefire → protection (Phase 3 reorder — a live, decaying threat outranks a
 * static reassurance). The top `STATUS_BANNER_MAX_FULL` render `variant="full"`; the rest render
 * `variant="compact"` — since war/ceasefire are mutually exclusive with each other, the worst
 * case is exactly 3 simultaneous banners, so only the last (always protection) ever compacts.
 */

const STATUS_BANNER_MAX_FULL = 2;

export interface ResolvedStatusBanner {
  tone: StatusBannerTone;
  icon: string;
  headline: string;
  detail?: string;
  href?: string;
  role?: 'alert' | 'status';
  variant: 'full' | 'compact';
}

interface UseThroneStatusBannersResult {
  banners: ResolvedStatusBanner[];
  /** Visually-hidden live-announcer text — set only on an absent→present tone transition. */
  announcement: string | null;
}

export function useThroneStatusBanners(province: Province): UseThroneStatusBannersResult {
  const kingdomId = province.metadata.location.kingdomId;
  const { data: dragonStatus, isLoading: dragonLoading, error: dragonError } = useDragonStatus(kingdomId);
  const { data: relationships, isLoading: relLoading, error: relError } = useKingdomRelationships(kingdomId);

  const warEntry = relationships?.relationships.find((r) => r.relationship === 'WAR') ?? null;

  const raw: Array<Omit<ResolvedStatusBanner, 'variant'>> = [];

  // 1. Dragon — only role === 'TARGET' on an ACTIVE project is a threat to this province;
  // an outgoing (ATTACKER) project isn't. Fail-quiet while loading/erroring: not an explicit
  // isLoading/error branch here, but incidental — useDragonStatus() only ever returns non-null
  // `data` once a fetch has actually resolved, so `dragonStatus` is null throughout its loading
  // and error states today. This would need revisiting if that hook is ever changed to preserve
  // stale data across a background refetch.
  if (dragonStatus?.state === 'ACTIVE' && dragonStatus?.role === 'TARGET') {
    raw.push({
      tone: 'dragon',
      icon: '🐉',
      headline: 'Dragon attack in progress',
      detail: 'Your kingdom is under attack by a dragon.',
      href: '/kingdom/dragons',
      role: 'alert',
    });
  }

  // 2. War / ceasefire — mutually exclusive with each other, independent of protection/dragon.
  // Fail-quiet while loading/erroring (same incidental-null-data caveat as the dragon banner
  // above, via useKingdomRelationships()); if the relationship resolves but carries no opponent
  // name, fall back to a generic headline rather than blocking.
  if (warEntry) {
    if (warEntry.inCeasefire) {
      raw.push({
        tone: 'ceasefire',
        icon: '🕊️',
        headline: 'Ceasefire',
        detail: warEntry.kingdomName
          ? `Ceasefire with ${warEntry.kingdomName}.`
          : 'Your kingdom is in a ceasefire.',
        role: 'status',
      });
    } else {
      raw.push({
        tone: 'war',
        icon: '⚔️',
        headline: 'At war',
        detail: warEntry.kingdomName
          ? `Your kingdom is at war with ${warEntry.kingdomName}.`
          : 'At war.',
        href: '/attack',
        role: 'alert',
      });
    }
  }

  // 3. Protection — province-level, least urgent (always the compact one in the 3-banner case).
  // Copy is scoped to the province level only — never a blanket "cannot attack or be attacked"
  // claim, since a province can be protected while its kingdom is still at war.
  const currentTick = province.sync.currentTick;
  const protectionExpiresAtTick = province.metadata.protectionExpiresAtTick;
  if (isProtected(protectionExpiresAtTick, currentTick)) {
    const daysRemaining = (protectionExpiresAtTick as number) - currentTick;
    raw.push({
      tone: 'protection',
      icon: '🛡️',
      headline: 'Protected',
      detail: `Your province cannot attack or be attacked for ${daysRemaining} more ${daysRemaining === 1 ? 'day' : 'days'}.`,
      role: 'status',
    });
  }

  const banners: ResolvedStatusBanner[] = raw.map((banner, index) => ({
    ...banner,
    variant: index < STATUS_BANNER_MAX_FULL ? 'full' : 'compact',
  }));

  // Live-region contract (Component 3): only the diff is announced — a tone that just
  // transitioned absent→present. A poll tick that re-confirms an already-shown tone announces
  // nothing (the effect below is keyed on the resolved tone-set, not on every render, so a
  // repeat poll that resolves to the identical tone-set never re-runs it — critically, this also
  // means the effect doesn't re-fire in response to its OWN `setAnnouncement` call, which would
  // otherwise immediately overwrite the just-set announcement with `null` on the very next
  // render). Fail-quiet loading/error branches never touch the announced tone-set, so a
  // transient refetch failure-then-recovery cannot produce a spurious repeat announcement.
  const previousTonesRef = useRef<Set<StatusBannerTone>>(new Set());
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const isTransient = dragonLoading || relLoading || Boolean(dragonError) || Boolean(relError);
  const toneKey = banners.map((b) => b.tone).join(',');

  useEffect(() => {
    if (isTransient) return;
    const newlyAppeared = banners.filter((b) => !previousTonesRef.current.has(b.tone));
    setAnnouncement(newlyAppeared.length > 0 ? newlyAppeared.map((b) => b.headline).join('. ') : null);
    previousTonesRef.current = new Set(banners.map((b) => b.tone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toneKey, isTransient]);

  return { banners, announcement };
}
